import {
  ArrowBackOutlined,
  ArrowForwardOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  createFilterOptions,
  MenuItem,
  Stack,
} from '@mui/material';
import type { AsyncResource } from '../../../shared/useAsyncResource';
import type {
  StaffingChangeSummary,
  StaffingHireReference,
  StaffingOrganization,
  StaffingWorker,
} from '../operations/model';
import {
  clearWorkSpaceValue,
  formatDateOnly,
  processLabel,
  type ChangeType,
  type DraftReference,
} from '../model/staffingPagePresentation';
import { IdentityAvatar, DialogSurface, FlowSteps, FormAutocomplete, FormGrid, FormGridItem, FormTextField, ValueBadge, DefinitionList, SectionCard } from '../../../design-system';
import { ChangeComparison } from './ChangeComparison';
import { BusinessTitleField } from './BusinessTitleField';
import { StaffingPlanChangeIdentity, StaffingPlanChangeList, StaffingPlanChangeRow } from './StaffingPlanChangeList';

const filterWorkers = createFilterOptions<StaffingWorker>({
  stringify: (worker) => `${worker.name} · ${worker.workerNumber} · ${worker.jobProfile || worker.title} · ${worker.organization}`,
});

export type StaffingComposerValues = {
  step: number;
  changeType: ChangeType;
  effectiveDate: string;
  workerSearch: string;
  selectedWorker?: StaffingWorker;
  organizationID: string;
  jobProfileID: string;
  jobLevelID: string;
  jobTypeID: string;
  locationID: string;
  workSpaceSelection: string;
  timeType: '' | 'FULL_TIME' | 'PART_TIME';
  businessTitle: string;
  businessTitleMode: 'KEEP' | 'CHANGE' | 'CLEAR';
  reason: string;
  submitLoading: boolean;
  submitError: string;
  createdDraft?: DraftReference;
  createAttempted: boolean;
  intentFrozen: boolean;
  selectedWorkerType: 'EMPLOYEE' | 'CONTINGENT_WORKER';
  currentLocationID: string;
  effectiveLocationID: string;
  previewChanges: NonNullable<StaffingChangeSummary['changes']>;
};

export type StaffingComposerResources = {
  workers: AsyncResource<StaffingWorker[]>;
  organizations: AsyncResource<StaffingOrganization[]>;
  locations: AsyncResource<StaffingHireReference[]>;
  jobs: AsyncResource<StaffingHireReference[]>;
  jobLevels: AsyncResource<StaffingHireReference[]>;
  jobTypes: AsyncResource<StaffingHireReference[]>;
  workSpaces: AsyncResource<StaffingHireReference[]>;
};

export type StaffingComposerActions = {
  close: () => void;
  changeType: (value: ChangeType) => void;
  changeEffectiveDate: (value: string) => void;
  changeWorkerSearch: (value: string) => void;
  selectWorker: (worker?: StaffingWorker) => void;
  changeOrganization: (value: string) => void;
  changeJobProfile: (value: string) => void;
  changeJobLevel: (value: string) => void;
  changeJobType: (value: string) => void;
  changeLocation: (value: string) => void;
  changeWorkSpace: (value: string) => void;
  changeTimeType: (value: '' | 'FULL_TIME' | 'PART_TIME') => void;
  changeBusinessTitle: (value: string) => void;
  changeBusinessTitleMode: (value: 'KEEP' | 'CHANGE' | 'CLEAR') => void;
  changeReason: (value: string) => void;
  reloadJobChangeReferences: () => void;
  moveToDetails: () => void;
  moveToBasics: () => void;
  persist: (action: 'SAVE' | 'SUBMIT') => void;
};

export type StaffingChangeComposerDialogProps = {
  open: boolean;
  values: StaffingComposerValues;
  resources: StaffingComposerResources;
  actions: StaffingComposerActions;
};

export function StaffingChangeComposerDialog({
  open,
  values,
  resources,
  actions,
}: StaffingChangeComposerDialogProps) {
  const {
    step,
    changeType,
    effectiveDate,
    workerSearch,
    selectedWorker,
    organizationID,
    jobProfileID,
    jobLevelID,
    jobTypeID,
    locationID,
    workSpaceSelection,
    timeType,
    businessTitle,
    businessTitleMode,
    reason,
    submitLoading,
    submitError,
    createdDraft,
    createAttempted,
    intentFrozen,
    selectedWorkerType,
    currentLocationID,
    effectiveLocationID,
    previewChanges,
  } = values;

  const selectedJob = resources.jobs.data?.find((job) => job.id === jobProfileID);
  const selectedJobLevel = resources.jobLevels.data?.find((level) => level.id === jobLevelID);
  const unresolvedTitleReference = resources.jobs.loading || resources.jobLevels.loading
    || Boolean(jobProfileID && !selectedJob?.descriptor?.trim())
    || Boolean(jobLevelID && !selectedJobLevel?.descriptor?.trim());
  const titleJobProfile = unresolvedTitleReference ? undefined : jobProfileID ? selectedJob?.descriptor : selectedWorker?.jobProfile;
  const titleJobLevel = jobLevelID ? selectedJobLevel?.descriptor : selectedWorker?.jobLevel;

  return <DialogSurface
    open={open}
    onClose={actions.close}
    busy={submitLoading}
    title="인사 발령 시작"
    help="시행일 기준 조직·인사 정보를 사용합니다. 초안은 현재 인사 기록에 반영되지 않으며, 승인된 발령은 시행일 기준으로 반영됩니다. 결재 설정에 따라 바로 승인되거나 승인자에게 전달됩니다."
    size="large"
    mobilePresentation="fullscreen"
    contentDividers
    headerContent={<FlowSteps activeStep={step} ariaLabel="인사 발령 단계" steps={['기본 정보', '변경 내용']} />}
    actions={step === 0 ? <>
      <Button onClick={actions.close} disabled={submitLoading}>취소</Button>
      <Button variant="contained" endIcon={<ArrowForwardOutlined />} onClick={actions.moveToDetails} disabled={resources.workers.loading}>변경 내용 입력</Button>
    </> : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={0.5}>
        <Button onClick={actions.close} disabled={submitLoading}>취소</Button>
        <Button startIcon={<ArrowBackOutlined />} onClick={actions.moveToBasics} disabled={submitLoading || intentFrozen}>기본 정보</Button>
      </Stack>
      <Box sx={{ flex: 1 }} />
      <Stack direction="row" spacing={0.75} sx={{ justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => actions.persist('SAVE')} disabled={submitLoading || Boolean(createdDraft)}>{submitLoading ? '저장 중…' : '초안 저장'}</Button>
        <Button variant="contained" onClick={() => actions.persist('SUBMIT')} disabled={submitLoading}>{submitLoading ? '요청 중…' : createdDraft ? '승인 요청 재시도' : '승인 요청'}</Button>
      </Stack>
    </Stack>}
  >
      <Stack spacing={2.25} sx={{ pt: 0.5 }}>
        {submitError && <Alert severity="error">{submitError}</Alert>}
        {createdDraft && <Alert severity="warning">초안은 저장됐습니다. 입력 내용은 고정되며 승인 요청만 다시 시도할 수 있습니다.</Alert>}
        {createAttempted && !createdDraft && <Alert severity="warning">초안 생성 결과를 확인하지 못했습니다. 입력 내용은 고정되며 같은 요청으로 저장 또는 승인 요청을 다시 시도할 수 있습니다.</Alert>}

        {step === 0 ? <SectionCard title="기본 정보" showDivider={false}>
          <Box sx={{ px: 2, pb: 2 }}>
          <FormGrid>
          <FormTextField required select label="발령 유형" value={changeType} disabled={intentFrozen} onChange={(event) => actions.changeType(event.target.value as ChangeType)}>
            <MenuItem value="JOB_CHANGE"><ValueBadge label="조직·직무 변경" /></MenuItem>
            <MenuItem value="LEAVE_OF_ABSENCE"><ValueBadge label="휴직" /></MenuItem>
            <MenuItem value="RETURN_FROM_LEAVE"><ValueBadge label="복직" /></MenuItem>
            <MenuItem value="END_WORKER"><ValueBadge label="퇴직·계약 종료" /></MenuItem>
          </FormTextField>
          <FormTextField required type="date" label="시행일" value={effectiveDate} disabled={intentFrozen} onChange={(event) => actions.changeEffectiveDate(event.target.value)} />
          <FormGridItem wide><FormAutocomplete
            options={resources.workers.data ?? []}
            filterOptions={filterWorkers}
            value={selectedWorker ?? null}
            loading={resources.workers.loading}
            disabled={!effectiveDate || intentFrozen}
            getOptionLabel={(worker) => `${worker.name} · ${worker.jobProfile || worker.title} · ${worker.organization}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={workerSearch.trim() ? '검색 결과가 없습니다.' : '구성원이 없습니다.'}
            loadingText="구성원 검색 중…"
            onInputChange={(_, value, inputReason) => { if (inputReason === 'input' || inputReason === 'clear') actions.changeWorkerSearch(value); }}
            onChange={(_, worker) => actions.selectWorker(worker ?? undefined)}
            input={{ required: true, label: '대상 구성원', placeholder: '이름 또는 사번으로 검색' }}
          /></FormGridItem>
          </FormGrid>
          {resources.workers.error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={resources.workers.reload}>다시 시도</Button>}>구성원 목록을 불러오지 못했습니다.</Alert>}
          </Box>
        </SectionCard> : <Stack spacing={2}>
          <SectionCard title="기본 정보" showDivider={false}>
            <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
              <DefinitionList items={[
                ['발령 유형', <ValueBadge label={processLabel(changeType)} />],
                ['시행일', formatDateOnly(effectiveDate)],
              ]} />
              <FormTextField label="변경 사유 (선택)" value={reason} disabled={intentFrozen} multiline minRows={3} slotProps={{ htmlInput: { maxLength: 4000 } }} onChange={(event) => actions.changeReason(event.target.value)} />
            </Stack>
          </SectionCard>
          {selectedWorker && changeType === 'JOB_CHANGE' && <SectionCard title="변경 입력" showDivider={false}>
            <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
              {changeType === 'JOB_CHANGE' && (resources.organizations.error || resources.jobs.error || resources.jobLevels.error || resources.jobTypes.error || resources.locations.error || resources.workSpaces.error) && <Alert severity="error" action={<Button color="inherit" size="small" onClick={actions.reloadJobChangeReferences}>다시 시도</Button>}>조직·직무·고용 유형·근무지·업무 공간 기준 정보를 불러오지 못했습니다.</Alert>}
              <FormGrid>
              {changeType === 'JOB_CHANGE' && <>
                  <FormTextField select label="이동 조직" value={organizationID} disabled={resources.organizations.loading || intentFrozen} sx={{ flex: 1, minWidth: 0, width: '100%' }} onChange={(event) => actions.changeOrganization(event.target.value)}>
                    <MenuItem value="">변경하지 않음</MenuItem>
                    {resources.organizations.data?.map((organization) => <MenuItem key={organization.id} value={organization.id}>{organization.name}</MenuItem>)}
                  </FormTextField>
                  <FormTextField select label="직무" value={jobProfileID} disabled={resources.jobs.loading || intentFrozen} sx={{ flex: 1, minWidth: 0, width: '100%' }} onChange={(event) => actions.changeJobProfile(event.target.value)}>
                    <MenuItem value="">변경하지 않음</MenuItem>
                    {resources.jobs.data?.map((job) => <MenuItem key={job.id} value={job.id}>{job.descriptor}</MenuItem>)}
                  </FormTextField>
                  <FormTextField select label={selectedWorkerType === 'EMPLOYEE' ? '고용 유형' : '외부 인력 유형'} value={jobTypeID} disabled={resources.jobTypes.loading || intentFrozen} sx={{ flex: 1, minWidth: 0, width: '100%' }} onChange={(event) => actions.changeJobType(event.target.value)}>
                    <MenuItem value="">변경하지 않음</MenuItem>
                    {resources.jobTypes.data?.map((jobType) => <MenuItem key={jobType.id} value={jobType.id}><ValueBadge label={jobType.descriptor} /></MenuItem>)}
                  </FormTextField>
                  <FormTextField select label="근무 형태" value={timeType} disabled={intentFrozen} sx={{ flex: 1, minWidth: 0, width: '100%' }} onChange={(event) => actions.changeTimeType(event.target.value as typeof timeType)}>
                    <MenuItem value="">변경하지 않음</MenuItem>
                    <MenuItem value="FULL_TIME"><ValueBadge label="전일제" /></MenuItem>
                    <MenuItem value="PART_TIME"><ValueBadge label="시간제" /></MenuItem>
                  </FormTextField>
                  <FormTextField select label="근무지" value={locationID} disabled={resources.locations.loading || intentFrozen} sx={{ flex: 1, minWidth: 0, width: '100%' }} onChange={(event) => { const nextLocationID = event.target.value; actions.changeLocation(nextLocationID); actions.changeWorkSpace(nextLocationID && nextLocationID !== currentLocationID && selectedWorker.workSpace ? clearWorkSpaceValue : ''); }}>
                    <MenuItem value="">변경하지 않음</MenuItem>
                    {resources.locations.data?.map((location) => <MenuItem key={location.id} value={location.id}>{location.descriptor}</MenuItem>)}
                  </FormTextField>
                  <FormTextField select label="업무 공간" value={workSpaceSelection} disabled={!effectiveLocationID || resources.workSpaces.loading || intentFrozen} sx={{ flex: 1, minWidth: 0, width: '100%' }} onChange={(event) => actions.changeWorkSpace(event.target.value)}>
                    <MenuItem value="">변경하지 않음</MenuItem>
                    {selectedWorker.workSpace ? <MenuItem value={clearWorkSpaceValue}>업무 공간 해제</MenuItem> : null}
                    {resources.workSpaces.data?.map((workSpace) => <MenuItem key={workSpace.id} value={workSpace.id}>{workSpace.descriptor}</MenuItem>)}
                  </FormTextField>
                  <FormTextField select label="직무 레벨" value={jobLevelID} disabled={resources.jobLevels.loading || intentFrozen} sx={{ flex: 1, minWidth: 0, width: '100%' }} onChange={(event) => actions.changeJobLevel(event.target.value)}>
                    <MenuItem value="">변경하지 않음</MenuItem>
                    {resources.jobLevels.data?.map((jobLevel) => <MenuItem key={jobLevel.id} value={jobLevel.id}><ValueBadge label={jobLevel.descriptor} /></MenuItem>)}
                  </FormTextField>
                  <FormTextField select label="직함 변경 방식" value={businessTitleMode} disabled={intentFrozen} onChange={(event) => actions.changeBusinessTitleMode(event.target.value as typeof businessTitleMode)}>
                    <MenuItem value="KEEP">현재 직함 유지</MenuItem><MenuItem value="CHANGE">직함 변경</MenuItem><MenuItem value="CLEAR">직함 해제</MenuItem>
                  </FormTextField>
                  <BusinessTitleField
                    value={businessTitle}
                    jobProfile={titleJobProfile}
                    jobLevel={titleJobLevel}
                    currentTitle={selectedWorker.businessTitle ?? ''}
                    disabled={intentFrozen || businessTitleMode === 'CLEAR'}
                    helperText={businessTitleMode === 'CLEAR' ? '직함을 해제하면 프로필에 직무가 표시됩니다.' : '입력하면 직함 변경으로 전환됩니다. 지우려면 직함 해제를 선택해 주세요.'}
                    onChange={actions.changeBusinessTitle}
                  />
              </>}
              </FormGrid>
            </Stack>
          </SectionCard>}
          <SectionCard title="변경 미리보기" showDivider={false} help={changeType === 'LEAVE_OF_ABSENCE' ? '휴직 종료일은 별도의 복직 발령으로 관리합니다.' : undefined}>
            <Box sx={{ px: 2, pb: 2 }}>
              {selectedWorker ? <StaffingPlanChangeList label="구성원별 발령 내용">
                <StaffingPlanChangeRow label={selectedWorker.name} identity={<StaffingPlanChangeIdentity
                  name={selectedWorker.name}
                  job={selectedWorker.jobProfile || selectedWorker.title}
                  organization={selectedWorker.organization}
                  avatar={<IdentityAvatar size={40} src={selectedWorker.profilePhotoUrl} alt="" aria-hidden>{selectedWorker.initials}</IdentityAvatar>}
                />}>
                  {previewChanges.length ? <ChangeComparison layout="inline" changes={previewChanges} /> : <Alert severity="warning">변경할 항목을 하나 이상 선택해 주세요.</Alert>}
                </StaffingPlanChangeRow>
              </StaffingPlanChangeList> : <Alert severity="warning">변경할 항목을 하나 이상 선택해 주세요.</Alert>}
            </Box>
          </SectionCard>
        </Stack>}
      </Stack>
  </DialogSurface>;
}
